import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Keys from '../../../../../Translations/generated/Checkout.de.json.keys';
import fallback from '../../../../../Translations/generated/Checkout.de.json';
import CommonWrapper, { RemountWrapper } from '../CommonWrapper';
import { useBookingService, useTranslation } from '@ibe/components';
import { ExtendedApiTraveler, useExtendedBooking } from '../ContextAndProvider';
import { ApiTraveler, ApiTravelerToJSON } from '@ibe/api';
import { Col, Container, Input, Label, Row } from 'reactstrap';
import PaymentPopUp from '../../PaymentPopUp';
import { addTravelDetailsEvent } from '@ibe/services';
import { useCollapsibleWizard } from '../../CollapsibleWizard/ContextAndProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const TravelersComponent = ({ idx, remount }: { idx: number; remount?: () => void }) => {
  const { selectedItems, setSelectedItems } = useExtendedBooking();

  const bs = useBookingService();
  const booking = bs.booking;
  const bookingTravelers = booking?.travelers;
  const [travelers, setTravelers] = useState<ApiTraveler[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { getCurrentIndex } = useCollapsibleWizard();
  const FORBIDDEN_NAME_CHARS = /[()<>/\\=:?]/;
  const { t } = useTranslation('Checkout', fallback);

  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleFocus = (fieldKey: string) => {
    setFocusedField(fieldKey);
  };

  const handleBlur = (travelerId: string, property: string) => {
    setFocusedField(null);
    setTouched(prev => ({ ...prev, [`${travelerId}_${property}`]: true }));
  };

  const getUpdatedTravelerInfo = (traveler: ExtendedApiTraveler): ExtendedApiTraveler => {
    const foundTraveler = bookingTravelers?.find(tr => tr.id === traveler.id);
    if (foundTraveler) {
      return {
        ...foundTraveler,
        position: traveler.position
      };
    }
    return traveler;
  };

  useEffect(() => {
    if (!!bookingTravelers && bookingTravelers.length > 0) {
      setTravelers([...bookingTravelers]);
      setSelectedItems({
        ...selectedItems,
        travelers: bookingTravelers,
        busStation: selectedItems.busStation
      });

      if (selectedItems.travelers) {
        const updatedSelectedItems = { ...selectedItems };

        if (updatedSelectedItems.roomsMain) {
          updatedSelectedItems.roomsMain = updatedSelectedItems.roomsMain.map(roomInfo => {
            return {
              ...roomInfo,
              travelers: roomInfo.travelers.map(getUpdatedTravelerInfo)
            };
          });
        }

        if (updatedSelectedItems.roomsOptional) {
          updatedSelectedItems.roomsOptional = updatedSelectedItems.roomsOptional.map(roomInfo => {
            return {
              ...roomInfo,
              travelers: roomInfo.travelers.map(getUpdatedTravelerInfo)
            };
          });
        }
        updatedSelectedItems.travelers = bookingTravelers;
        setSelectedItems(updatedSelectedItems);
      }
    }
  }, [bookingTravelers]);

  const onNext = async () => {
    addTravelDetailsEvent.broadcast({
      step: `${getCurrentIndex() + 1}. ` + t(Keys.extBookingTravelers),
      booking: bs.booking
    });
    await bs.updateTravelers(travelers.map(traveler => ApiTravelerToJSON({ ...traveler })));
    setSelectedItems({ ...selectedItems, travelers });
  };

  const handleTravelerChange = (
    e: ChangeEvent<HTMLInputElement>,
    property: string,
    traveler: ApiTraveler
  ) => {
    const newTravelers = [...travelers];
    const newTravelerIndex = newTravelers.findIndex(
      singleTraveler => singleTraveler.id === traveler.id
    );
    newTravelers.splice(newTravelerIndex, 1, {
      ...newTravelers[newTravelerIndex],
      [property]: e.target.value
    });
    if (newTravelerIndex >= 0) {
      setTravelers(newTravelers);
    }
  };

  const isInvalidName = (name: string | null | undefined): boolean => {
    if (!name || name.trim() === '') return true;
    return FORBIDDEN_NAME_CHARS.test(name);
  };

  const disableBtn = useCallback((): boolean => {
    return travelers.some(
      traveler => isInvalidName(traveler.firstName) || isInvalidName(traveler.lastName)
    );
  }, [travelers]);

  return (
    <CommonWrapper
      idx={idx}
      description={t(Keys.extBookingEnterTravelers)}
      title={t(Keys.extBookingTravelers)}
      successNotes={[t(Keys.extBookingEnteredTravelers)]}
      onNext={onNext}
      disableButton={disableBtn()}
      jumpToNextMissingSuccessfulStep
      onClose={remount}
    >
      <Container className="checkout__extras traveler p-0">
        {travelers.map((traveler: ApiTraveler, tIndex: number) => {
          const firstNameKey = `${traveler.id}_firstName`;
          const lastNameKey = `${traveler.id}_lastName`;

          const firstNameInvalid = isInvalidName(traveler.firstName);
          const lastNameInvalid = isInvalidName(traveler.lastName);

          const firstNameHasValue = !!traveler.firstName && traveler.firstName.trim() !== '';
          const lastNameHasValue = !!traveler.lastName && traveler.lastName.trim() !== '';

          const firstNameError =
            touched[firstNameKey] && firstNameInvalid && focusedField !== firstNameKey;
          const lastNameError =
            touched[lastNameKey] && lastNameInvalid && focusedField !== lastNameKey;

          const firstNameSuccess =
            !firstNameInvalid && firstNameHasValue && focusedField !== firstNameKey;
          const lastNameSuccess =
            !lastNameInvalid && lastNameHasValue && focusedField !== lastNameKey;

          return (
            <Row key={'traveler' + tIndex} className="traveler-row">
              <Col
                className={`checkout__extras__name-form__label ${
                  tIndex === 0 ? 'checkout__extras__name-form__label--first' : ''
                }`}
                xs={12}
                lg={3}
              >
                <div>
                  <p className="traveler-headline mb-0">
                    {`${tIndex + 1}. ${t(Keys.person)}`}&nbsp;&nbsp;
                  </p>
                  {tIndex === 0 && (
                    <span>
                      <span className="customer">{t(Keys.customer)}</span>
                      <PaymentPopUp text={t(Keys.customerInfo)} />
                    </span>
                  )}
                </div>
              </Col>
              <Col
                xs={12}
                sm={6}
                lg={4}
                className={`form-input-container form-input offset-lg-1 ${
                  firstNameError ? 'input--error form-input--errors' : ''
                } ${firstNameSuccess ? 'form-input--success' : ''}`}
              >
                <Label
                  for={`traveler-${traveler.id}-firstname`}
                  className={`search-form__label font-weight-medium ${
                    firstNameError ? 'form__error__top' : ''
                  }`}
                >
                  {t(Keys.firstname)}
                </Label>
                <div className="position-relative">
                  <Input
                    id={`traveler-${traveler.id}-firstname`}
                    value={traveler.firstName || ''}
                    onFocus={() => handleFocus(firstNameKey)}
                    onBlur={() => handleBlur(traveler.id, 'firstName')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleTravelerChange(e, 'firstName', traveler)
                    }
                  />
                  {firstNameError && (
                    <>
                      <span className="form__error form__error--traveler">
                        {FORBIDDEN_NAME_CHARS.test(traveler.firstName || '')
                          ? t(Keys.nameError)
                          : t(Keys.firstnameEmpty)}
                      </span>
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="form-input__error-icon"
                      />
                    </>
                  )}
                  {firstNameSuccess && (
                    <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />
                  )}
                </div>
              </Col>

              <Col
                xs={12}
                sm={6}
                lg={4}
                className={`form-input-container form-input ${
                  lastNameError ? 'input--error form-input--errors' : ''
                } ${lastNameSuccess ? 'form-input--success' : ''}`}
              >
                <Label
                  for={`traveler-${traveler.id}-lastname`}
                  className={`search-form__label font-weight-medium ${
                    lastNameError ? 'form__error__top' : ''
                  }`}
                >
                  {t(Keys.lastname)}
                </Label>
                <div className="position-relative">
                  <Input
                    id={`traveler-${traveler.id}-lastname`}
                    value={traveler.lastName || ''}
                    onFocus={() => handleFocus(lastNameKey)}
                    onBlur={() => handleBlur(traveler.id, 'lastName')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleTravelerChange(e, 'lastName', traveler)
                    }
                  />
                  {lastNameError && (
                    <>
                      <span className="form__error form__error--traveler">
                        {FORBIDDEN_NAME_CHARS.test(traveler.lastName || '')
                          ? t(Keys.nameError)
                          : t(Keys.lastnameEmpty)}
                      </span>
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="form-input__error-icon"
                      />
                    </>
                  )}
                  {lastNameSuccess && (
                    <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />
                  )}
                </div>
              </Col>
            </Row>
          );
        })}
      </Container>
    </CommonWrapper>
  );
};

export const Travelers = ({ idx }: { idx: number }) => (
  <RemountWrapper Component={TravelersComponent} props={{ idx }} />
);
